import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { initAnalytics } from './lib/analytics';
import StickyContactBar from './components/StickyContactBar';
import { AuthCallback } from './components/AuthCallback';
import AuthPage from './pages/AuthPage';
import AccountPage from './pages/AccountPage';
import AdminPage from './pages/AdminPage';
import NotFoundPage from './pages/NotFoundPage';
import HomeV2 from './pages/HomeV2';
import CommercialPoolRobotPage from './pages/CommercialPoolRobotPage';
import PoolCleaningRobotsPage from './pages/PoolCleaningRobotsPage';
import FloorCleaningRobotsPage from './pages/FloorCleaningRobotsPage';
import WindowCleaningRobotsPage from './pages/WindowCleaningRobotsPage';
import RoboticLawnMowersPage from './pages/RoboticLawnMowersPage';
import ProductDetail from './pages/ProductDetail';
import DealerPage from './pages/DealerPage';
import AboutUsPage from './pages/AboutUsPage';
import PolicyPage from './pages/PolicyPage';
import CityPage from './pages/CityPage';
import IndustryPage from './pages/IndustryPage';
import cityPages from './data/cityPages.json';
import KeywordLandingPage from './pages/KeywordLandingPage';
import keywordPages from './data/keywordPages.json';
import TopicHubPage from './pages/TopicHubPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AccessoriesPage from './pages/AccessoriesPage';
import SupportPage from './pages/SupportPage';
import { Toaster } from './components/ui/toaster';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import './App.css';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent Auth callback (session_id in URL fragment) before normal routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
            <Route path="/" element={<HomeV2 />} />
            {/* Category Pages */}
            <Route path="/pool-cleaning-robots" element={<PoolCleaningRobotsPage />} />
            <Route path="/floor-cleaning-robots" element={<FloorCleaningRobotsPage />} />
            <Route path="/window-cleaning-robots" element={<WindowCleaningRobotsPage />} />
            <Route path="/robotic-lawn-mowers" element={<RoboticLawnMowersPage />} />
            {/* Legacy category landing URLs (indexed SEO pages — render matching category UI) */}
            <Route path="/products/floor-cleaning-robot" element={<FloorCleaningRobotsPage />} />
            <Route path="/products/window-cleaning-robot" element={<WindowCleaningRobotsPage />} />
            <Route path="/products/pool-cleaning-robot" element={<PoolCleaningRobotsPage />} />
            <Route path="/products/lawn-cleaning-robot" element={<RoboticLawnMowersPage />} />
            <Route path="/products/commercial-cleaning-robot" element={<FloorCleaningRobotsPage />} />
            {/* Product Pages (data-driven template) */}
            <Route path="/accessories" element={<AccessoriesPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/products/:slug" element={<ProductDetail />} />
            {/* Commercial Pages */}
            <Route path="/commercial-pool-cleaning-robot-india" element={<CommercialPoolRobotPage />} />
            <Route path="/commercial-floor-cleaning-robots" element={<FloorCleaningRobotsPage />} />
            <Route path="/residential-pool-cleaning-robots" element={<PoolCleaningRobotsPage />} />
            {/* Dealer */}
            <Route path="/dealer" element={<DealerPage />} />
            {/* Company & Policies */}
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/privacy-policy" element={<PolicyPage />} />
            <Route path="/terms-of-service" element={<PolicyPage />} />
            <Route path="/shipping-policy" element={<PolicyPage />} />
            <Route path="/warranty-policy" element={<PolicyPage />} />
            <Route path="/return-refund-policy" element={<PolicyPage />} />
            <Route path="/customer-policy" element={<PolicyPage />} />
            <Route path="/cookie-policy" element={<PolicyPage />} />
            {/* City & Industry SEO landing pages */}
            {cityPages.map((c) => (
              <Route key={c.slug} path={`/cleaning-robots-in-${c.slug}`} element={<CityPage slug={c.slug} />} />
            ))}
            <Route path="/solutions/:industry" element={<IndustryPage />} />
            {/* Keyword SEO landing pages */}
            {keywordPages.map((k) => (
              <Route key={k.slug} path={`/${k.slug}`} element={<KeywordLandingPage slug={k.slug} />} />
            ))}
            {/* Auth & Account */}
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/register" element={<AuthPage mode="register" />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/admin" element={<AdminPage />} />
            {/* Blog */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/topics/:cluster" element={<TopicHubPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            {/* Catch-all 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
      <StickyContactBar />
    </>
  );
}

function App() {
  useEffect(() => {
    initAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount; initAnalytics is a stable module import

  return (
    <AuthProvider>
      <CartProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster />
        </div>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
